import React, { Fragment, useCallback, useEffect, useState } from "react";

import {
  Button,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Modal,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MainCard from "components/common/card/MainCard";
import CloseIcon from "@mui/icons-material/Close";
import Request from "../../../../core/requests";
import { openSnackbar } from "store/slices/snackbar";
import { useDispatch, useSelector } from "react-redux";
import SubCard from "components/common/card/SubCard";
import { grey } from "@mui/material/colors";
import { DateTime } from "luxon";
import { getTimeZoneStandard } from "utils/timezone";
import { logout } from "store/user.store";
import { refreshToken } from "store/user.store";
import { useTranslation } from "react-i18next";

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const SimpleModal = ({
  handleClose = () => { },
  open,
  id,
  isChageStatusSensor
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch();
  const { token, user, loading: loadingRefresh } = useSelector((state) => state.user);
  const theme = useTheme();
  const matcheXS = useMediaQuery(theme.breakpoints.down("sm"));
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [data, setData] = useState(null);
  const [modalStyle] = useState(getModalStyle);
  const [labelDate, setLabelDate] = useState("");
  const [dataSubmit, setDataSubmit] = useState(null);
  const [update, setUpdate] = useState(null);

  const onFailure = useCallback((error) => {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message ? error.response.data.message : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error) => {
      if (isFirstRefresh) {
        if (error?.response?.status === 403) {
          dispatch(logout());
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }

      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onSuccess = useCallback((resp) => {
    setData(resp);
    setIsFirstRefresh(true);
    setUpdate(true);

    // eslint-disable-next-line
  }, [i18n, t]);

  const onChangeStatus = useCallback((id, state) => {
    if (!loadingRefresh) {
      setIsFirstRefresh(true)
      setUpdate(false);
      setDataSubmit({ id: id, state: state });
      Request.stations
        .setSensorStationStatus(
          { sensor_id: id, token: token },
          { set_active: state }
        )
        .then((resp) => {
          onSuccess(resp);
        })
        .catch((error) => {
          onError(error, { id: id, state: state })
        });
    }
    // eslint-disable-next-line
  }, [token, loadingRefresh]);

  useEffect(() => {
    const idStation = isChageStatusSensor?.id ? isChageStatusSensor?.id : id
    if (idStation && !loadingRefresh) {
      Request.stations
        .getSensorStation({ id: idStation, token: token })
        .then((resp) => {
          onSuccess(resp)
          setData(resp);
          setIsFirstRefresh(true);
        })
        .catch((error) => onError(error));
    }
    // eslint-disable-next-line
  }, [id, token, update, loadingRefresh, isFirstRefresh, isChageStatusSensor]);

  useEffect(() => {
    if (data) {
      if (data?.edt && data?.edt !== null) {
        const date = DateTime.fromISO(getTimeZoneStandard(data?.edt));
        const dateTen = DateTime.now().plus({ years: 10 });

        if (date.year >= dateTen.year) {
          setLabelDate(`10+ ${t('years')}`);
        } else {
          setLabelDate(
            DateTime.fromISO(getTimeZoneStandard(data?.edt)).toLocaleString(
              { month: "long", year: "numeric" },
              {
                locale: i18n?.language ? i18n.language : "it",
              })
          );
        }
      } else {
        setLabelDate('-')
      }

    }
  }, [data, t, i18n]);

  useEffect(() => {
    if (!isFirstRefresh && !loadingRefresh && dataSubmit) {
      onChangeStatus(dataSubmit.id, dataSubmit.state);
    }
    // eslint-disable-next-line
  }, [isFirstRefresh, loadingRefresh, dataSubmit]);

  return (
    <Fragment>
      {/* {loading && <Loading />} */}
      <Modal
        open={open}
        onClose={handleClose}
      >
        <MainCard
          style={modalStyle}
          sx={{
            position: "absolute",
            width: matcheXS ? "90%" : "40%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: 450,
          }}
          title={t('Details Environmental Sensor')}
          content={false}
          secondary={
            <IconButton onClick={handleClose} size="large">
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <CardContent>
            <Grid container spacing={1}>
              <Grid
                item
                xs={12}
                sx={{
                  overflow: "auto",
                }}
              >
                <TableContainer>
                  <SubCard
                    sx={{
                      bgcolor:
                        theme.palette.mode === "dark" ? "dark.800" : "grey.50",
                      mb: 2,
                      overflow: "auto",
                    }}
                    content={false}
                  >
                    <Table size="small" aria-label="purchases">
                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Sensor ID')}</Typography>
                        </TableCell>
                        <TableCell> {data?.sensorID}</TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Status')}</Typography>
                        </TableCell>
                        <TableCell>
                          {data?.isActive ? (
                            <Chip
                              label={t('Active')}
                              sx={{
                                backgroundColor: "#B9F6CA",
                                color: "#00C853",
                              }}
                              size="small"
                            />
                          ) : (
                            <Chip label={t('Inactive')} size="small" />
                          )}
                        </TableCell>

                        {/*<TableCell>  TODO: SOLO DEMO*/}
                        {/*  <Button*/}
                        {/*    onClick={() =>*/}
                        {/*      onChangeStatus(data?.sensorID, !data?.isActive)*/}
                        {/*    }*/}
                        {/*  >*/}
                        {/*    {data?.isActive ? t('Deactivate') : t('Activate')}*/}
                        {/*  </Button>*/}
                        {/*</TableCell>*/}
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Name')}</Typography>
                        </TableCell>
                        <TableCell> {data?.name ? data?.name : "-"}</TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Model')}</Typography>
                        </TableCell>
                        <TableCell> {data?.model ? data?.model : "-"}</TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">
                            {t('Sending interval')}
                          </Typography>
                        </TableCell>
                        <TableCell>

                          {data?.sendInterval
                            ? `${data?.sendInterval} ${t('seconds')}`
                            : "-"}
                        </TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">EDT</Typography>
                        </TableCell>
                        <TableCell align="left" sx={{ textTransform: 'capitalize' }}>
                          {labelDate}
                        </TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('AWS ID')}</Typography>
                        </TableCell>
                        <TableCell> {data?.awsID ? data?.awsID : "-"}</TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Software Version')}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          {data?.swVersion ? data?.swVersion : "-"}
                        </TableCell>
                        <TableCell />
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ borderRight: `1px solid ${grey[200]}` }}>
                          <Typography variant="h5">{t('Hardware Version')}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          {data?.hwVersion ? data?.hwVersion : "-"}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </Table>
                  </SubCard>
                </TableContainer>
              </Grid>
            </Grid>
          </CardContent>
        </MainCard>
      </Modal>
    </Fragment>
  );
};

export default SimpleModal;
